import { LitElement, html, css } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { featureStorage } from '../../storage/feature-storage.js'
import { routeStorage } from '../../storage/route-storage.js'
import type { Feature } from '../../schemas/feature.js'
import type { Difficulty } from '../../schemas/difficulty.js'
import '@material/web/dialog/dialog.js'
import '@material/web/button/text-button.js'
import '@material/web/button/filled-button.js'
import '@material/web/textfield/outlined-text-field.js'
import '@material/web/checkbox/checkbox.js'
import '@material/web/select/outlined-select.js'
import '@material/web/select/select-option.js'
import type { MdDialog } from '@material/web/dialog/dialog.js'
import type { MdOutlinedTextField } from '@material/web/textfield/outlined-text-field.js'
import type { MdCheckbox } from '@material/web/checkbox/checkbox.js'
import type { MdOutlinedSelect } from '@material/web/select/outlined-select.js'

@customElement('route-promote-dialog')
export class RoutePromoteDialog extends LitElement {
  static override styles = css`
    :host {
      position: fixed;
      z-index: 3000;
      pointer-events: none;
    }

    md-dialog {
      --md-dialog-container-color: #1e293b;
      --md-dialog-headline-color: white;
      --md-dialog-supporting-text-color: rgba(255, 255, 255, 0.8);
      --md-sys-color-outline: rgba(255, 255, 255, 0.2);
      --md-dialog-container-shape: 12px;
      pointer-events: all;
      max-width: 500px;
    }

    md-text-button {
      --md-text-button-label-text-color: rgba(255, 255, 255, 0.7);
      --md-text-button-hover-label-text-color: white;
    }

    md-filled-button {
      --md-filled-button-container-color: var(--cairn-primary-color, #3b82f6);
      --md-filled-button-label-text-color: white;
    }

    md-outlined-text-field {
      --md-outlined-text-field-input-text-color: white;
      --md-outlined-text-field-label-text-color: rgba(255, 255, 255, 0.7);
      --md-outlined-text-field-outline-color: rgba(255, 255, 255, 0.3);
      --md-outlined-text-field-focus-outline-color: var(--cairn-primary-color, #3b82f6);
      --md-outlined-text-field-hover-outline-color: rgba(255, 255, 255, 0.5);
      width: 100%;
    }

    md-outlined-select {
      --md-outlined-select-text-field-input-text-color: white;
      --md-outlined-select-text-field-label-text-color: rgba(255, 255, 255, 0.7);
      --md-outlined-select-text-field-outline-color: rgba(255, 255, 255, 0.3);
      --md-outlined-select-text-field-focus-outline-color: var(--cairn-primary-color, #3b82f6);
      width: 100%;
    }

    .form-fields {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .feature-list {
      max-height: 200px;
      overflow-y: auto;
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      padding: 8px;
    }

    .feature-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 4px;
      border-radius: 4px;
    }

    .feature-item:hover {
      background: rgba(255, 255, 255, 0.05);
    }

    .feature-name {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.9);
    }

    .feature-type {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.5);
      margin-left: auto;
    }

    .section-label {
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: rgba(255, 255, 255, 0.5);
      margin-bottom: 4px;
    }

    .select-all {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px;
      font-size: 13px;
      color: rgba(255, 255, 255, 0.7);
      cursor: pointer;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      margin-bottom: 4px;
      padding-bottom: 8px;
    }
  `

  @property({ type: String }) planId = ''
  @property({ type: String }) planName = ''
  @property({ type: String }) createdBy = ''

  @state() private features: Feature[] = []
  @state() private selectedFeatureIds = new Set<string>()
  @state() private saving = false

  private resolvePromise?: ((routeId: string | null) => void) | undefined

  async show(): Promise<string | null> {
    this.features = await featureStorage.getFeaturesForEntity('plan', this.planId)
    this.selectedFeatureIds = new Set(this.features.map(f => f.id))
    this.saving = false

    const dialog = this.shadowRoot?.querySelector('md-dialog') as MdDialog | null
    if (!dialog) return null

    return new Promise<string | null>(resolve => {
      this.resolvePromise = resolve
      void dialog.show()
    })
  }

  private handleClose(e: Event): void {
    const dialog = e.target as MdDialog
    if (dialog.returnValue !== 'confirm') {
      this.resolvePromise?.(null)
      this.resolvePromise = undefined
    }
  }

  private toggleFeature(id: string, checked: boolean): void {
    const next = new Set(this.selectedFeatureIds)
    if (checked) {
      next.add(id)
    } else {
      next.delete(id)
    }
    this.selectedFeatureIds = next
  }

  private toggleAll(checked: boolean): void {
    if (checked) {
      this.selectedFeatureIds = new Set(this.features.map(f => f.id))
    } else {
      this.selectedFeatureIds = new Set()
    }
  }

  private async handleSave(): Promise<void> {
    if (this.saving) return
    this.saving = true

    const nameField = this.shadowRoot?.querySelector('#route-name') as MdOutlinedTextField | null
    const descField = this.shadowRoot?.querySelector('#route-desc') as MdOutlinedTextField | null
    const diffSelect = this.shadowRoot?.querySelector('#route-diff') as MdOutlinedSelect | null

    const name = nameField?.value.trim() ?? this.planName
    const description = descField?.value.trim() ?? undefined
    const difficulty = diffSelect?.value ? (diffSelect.value as Difficulty) : undefined

    const result = await routeStorage.promoteFromPlan(this.planId, {
      name,
      description,
      difficulty,
      featureIds: [...this.selectedFeatureIds],
      createdBy: this.createdBy,
    })

    const dialog = this.shadowRoot?.querySelector('md-dialog') as MdDialog | null
    if (result.success && result.data) {
      this.resolvePromise?.(result.data.id)
    } else {
      this.resolvePromise?.(null)
    }
    this.resolvePromise = undefined
    void dialog?.close('confirm')
  }

  override render(): unknown {
    const allSelected = this.selectedFeatureIds.size === this.features.length
    const noneSelected = this.selectedFeatureIds.size === 0

    return html`
      <md-dialog @closed=${this.handleClose}>
        <div slot="headline">Save as Route</div>
        <div slot="content">
          <div class="form-fields">
            <md-outlined-text-field
              id="route-name"
              label="Route name"
              value=${this.planName}
            ></md-outlined-text-field>

            <md-outlined-text-field
              id="route-desc"
              label="Description (optional)"
              type="textarea"
              rows="2"
            ></md-outlined-text-field>

            <md-outlined-select id="route-diff" label="Difficulty (optional)">
              <md-select-option value=""></md-select-option>
              <md-select-option value="beginner">
                <div slot="headline">Beginner</div>
              </md-select-option>
              <md-select-option value="intermediate">
                <div slot="headline">Intermediate</div>
              </md-select-option>
              <md-select-option value="advanced">
                <div slot="headline">Advanced</div>
              </md-select-option>
              <md-select-option value="expert">
                <div slot="headline">Expert</div>
              </md-select-option>
            </md-outlined-select>

            ${this.features.length > 0
              ? html`
                  <div>
                    <div class="section-label">Features to include</div>
                    <div class="feature-list">
                      <label class="select-all">
                        <md-checkbox
                          ?checked=${allSelected}
                          ?indeterminate=${!allSelected && !noneSelected}
                          @change=${(e: Event) => this.toggleAll((e.target as MdCheckbox).checked)}
                        ></md-checkbox>
                        Select all
                      </label>
                      ${this.features.map(
                        f => html`
                          <label class="feature-item">
                            <md-checkbox
                              ?checked=${this.selectedFeatureIds.has(f.id)}
                              @change=${(e: Event) =>
                                this.toggleFeature(f.id, (e.target as MdCheckbox).checked)}
                            ></md-checkbox>
                            <span class="feature-name">${f.name}</span>
                            <span class="feature-type">${f.type}</span>
                          </label>
                        `
                      )}
                    </div>
                  </div>
                `
              : ''}
          </div>
        </div>
        <div slot="actions">
          <md-text-button
            @click=${() => {
              void (this.shadowRoot?.querySelector('md-dialog') as MdDialog).close('cancel')
            }}
          >
            Cancel
          </md-text-button>
          <md-filled-button
            ?disabled=${noneSelected || this.saving}
            @click=${() => void this.handleSave()}
          >
            ${this.saving ? 'Saving...' : 'Save as Route'}
          </md-filled-button>
        </div>
      </md-dialog>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'route-promote-dialog': RoutePromoteDialog
  }
}
